import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const DeleteAccount: React.FC = () => {
    return (
        <>
            <SEOHead
                title="Delete Account"
                description="Learn how to delete your account. Follow these simple steps to permanently remove your account and data."
            />
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-800 mb-4">Delete Account</h1>
                        <p className="text-lg text-gray-600">
                            We're sorry to see you go. Follow the steps below to delete your account.
                        </p>
                    </div>

                    {/* Method 1: Delete from App */}
                    <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                1
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Method 1: Delete from App (Recommended)</h2>
                        </div>

                        <p className="text-gray-600 mb-6">
                            You can delete your account directly from the app using these simple steps:
                        </p>

                        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-6">
                            <h3 className="font-bold text-blue-800 mb-4">Steps:</h3>
                            <ol className="space-y-3 text-gray-700">
                                <li className="flex items-start gap-3">
                                    <span className="font-bold text-blue-600">1.</span>
                                    <span>Open the <strong>Quicklish</strong> app</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="font-bold text-blue-600">2.</span>
                                    <span>Go to <strong>Profile → Delete Account</strong></span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="font-bold text-blue-600">3.</span>
                                    <span>Type <strong className="text-red-600">"DELETE"</strong> to confirm</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="font-bold text-blue-600">4.</span>
                                    <span>Your account and all data will be permanently deleted</span>
                                </li>
                            </ol>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-800">
                                <strong>⚠️ Warning:</strong> This action is permanent and cannot be undone. All your data including progress, favorites, and memorized items will be deleted.
                            </p>
                        </div>
                    </div>

                    {/* Method 2: Request via Email */}
                    <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                2
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Method 2: Request via Email</h2>
                        </div>

                        <p className="text-gray-600 mb-6">
                            If you cannot access the app, you can send us an email with your deletion request:
                        </p>

                        <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-6 mb-6">
                            <h3 className="font-bold text-green-800 mb-4">Email Instructions:</h3>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm font-semibold text-gray-700 mb-2">Send Deletion Request Email:</p>
                                    <div className="bg-white border border-gray-300 rounded-lg p-4">
                                        <a
                                            href="mailto:learn.with.me.beginning@gmail.com?subject=Account%20Deletion%20Request"
                                            className="text-blue-600 hover:text-blue-800 font-mono break-all underline"
                                        >
                                            learn.with.me.beginning@gmail.com
                                        </a>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-sm font-semibold text-gray-700 mb-2">Or email us directly at:</p>
                                    <div className="bg-white border border-gray-300 rounded-lg p-4">
                                        <p className="text-gray-800 font-mono break-all">
                                            learn.with.me.beginning@gmail.com
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-sm font-semibold text-gray-700 mb-2">Subject:</p>
                                    <div className="bg-white border border-gray-300 rounded-lg p-4">
                                        <p className="text-gray-800 font-semibold">
                                            Account Deletion Request
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-2">What to Include in Your Email:</h4>
                            <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                                <li>Your account email address</li>
                                <li>Your user ID (if available)</li>
                                <li>Confirmation that you want to delete your account</li>
                                <li>Any additional information that might help us process your request</li>
                            </ul>
                        </div>
                    </div>

                    {/* Additional Information */}
                    <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">What Gets Deleted?</h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <h3 className="font-semibold text-red-800 mb-2">Account Data</h3>
                                <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                                    <li>Profile information</li>
                                    <li>Account settings</li>
                                    <li>User preferences</li>
                                </ul>
                            </div>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <h3 className="font-semibold text-red-800 mb-2">Learning Data</h3>
                                <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                                    <li>All memorized items</li>
                                    <li>Favorites and bookmarks</li>
                                    <li>Completed exercises</li>
                                    <li>Learning progress</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h2>
                        <div className="space-y-6">
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-2">How long does it take to delete my account?</h3>
                                <p className="text-gray-600 text-sm">
                                    If you delete from the app, your account is deleted immediately. Email requests are typically processed within 24-48 hours.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-2">Can I recover my account after deletion?</h3>
                                <p className="text-gray-600 text-sm">
                                    No, account deletion is permanent and cannot be undone. Please make sure you want to delete your account before proceeding.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-2">What if I change my mind?</h3>
                                <p className="text-gray-600 text-sm">
                                    If you haven't confirmed the deletion yet, you can simply close the page. Once you type "DELETE" and confirm, the process cannot be reversed.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-2">Will my data be backed up?</h3>
                                <p className="text-gray-600 text-sm">
                                    No, all your data will be permanently removed from our servers. We recommend exporting any important information before deleting your account.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Contact Support */}
                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl shadow-lg p-8 text-white text-center">
                        <h2 className="text-2xl font-bold mb-4">Need Help?</h2>
                        <p className="mb-6 opacity-90">
                            If you have questions or need assistance with account deletion, our support team is here to help.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="mailto:learn.with.me.beginning@gmail.com"
                                className="bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-200"
                            >
                                Contact Support
                            </a>
                            <Link
                                to="/help"
                                className="bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/30 transition-colors duration-200 border border-white/30"
                            >
                                Visit Help Center
                            </Link>
                        </div>
                    </div>

                    {/* Back Button */}
                    <div className="text-center mt-8">
                        <Link
                            to="/home"
                            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 font-semibold transition-colors duration-200"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DeleteAccount;

